import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
  CircularProgress,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Badge,
} from '@mui/material';
import { Refresh as RefreshIcon, Visibility as VisibilityIcon, Wifi as WifiIcon, WifiOff as WifiOffIcon } from '@mui/icons-material';
import { useAIObservatory, ObservatoryStatistics, AgentConfiguration, AgentInstance, RegistryOverview } from './hooks/useAIObservatory';
import { StatisticsPanel } from './StatisticsPanel';
import { AgentRegistryPanel } from './AgentRegistryPanel';
import { AgentInstancesPanel } from './AgentInstancesPanel';
import { ActivityStreamPanel } from './ActivityStreamPanel';
import { HistoryPanel } from './HistoryPanel';
import { PerformancePanel } from './PerformancePanel';
import { getBaseUrl } from '../../../framework/config/api.config';
import { apiClient } from '../../../framework/api/api.client';

export const AIObservatoryView: React.FC = () => {
  // API Configuration - use framework's standard API URL resolution
  const apiUrl = getBaseUrl();

  // WebSocket connection for real-time updates
  const { connected, snapshot, events, clearEvents } = useAIObservatory(apiUrl);

  // State for REST API data
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [statistics, setStatistics] = useState<ObservatoryStatistics | null>(null);
  const [configurations, setConfigurations] = useState<AgentConfiguration[]>([]);
  const [instances, setInstances] = useState<AgentInstance[]>([]);
  const [registry, setRegistry] = useState<RegistryOverview | null>(null);

  // View state
  const [selectedTab, setSelectedTab] = useState(0);

  // Load data from REST API using authenticated apiClient
  const loadData = async (silent = false) => {
    if (!silent) setLoading(true);
    setRefreshing(true);
    
    try {
      const [statsRes, configsRes, instancesRes, registryRes] = await Promise.all([
        apiClient.call('/ai-observatory/statistics'),
        apiClient.call('/ai-observatory/configurations'),
        apiClient.call('/ai-observatory/instances'),
        apiClient.call('/ai-observatory/registry'),
      ]);

      setStatistics(statsRes.statistics);
      setConfigurations(configsRes.configurations);
      setInstances(instancesRes.instances);
      setRegistry(registryRes.registry);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error loading observatory data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadData();
    
    // Auto-refresh statistics every 30 seconds (silent refresh)
    const refreshInterval = setInterval(() => {
      // Only refresh statistics, not configs/instances/registry (they change rarely)
      apiClient.call('/ai-observatory/statistics').then(statsRes => {
        setStatistics(statsRes.statistics);
        setLastRefresh(new Date());
      }).catch(error => {
        console.error('Error refreshing statistics:', error);
      });
    }, 30000); // 30 seconds
    
    return () => clearInterval(refreshInterval);
  }, []);

  const handleRefresh = async () => {
    await loadData();
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <VisibilityIcon />
          <Typography variant="h4">AI Observatory</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Chip
            icon={connected ? <WifiIcon /> : <WifiOffIcon />}
            label={connected ? 'Connected' : 'Disconnected'}
            color={connected ? 'success' : 'error'}
            variant="outlined"
          />
          {events.length > 0 && (
            <Badge badgeContent={events.length} color="primary">
              <Typography variant="body2">Events</Typography>
            </Badge>
          )}
          <Chip 
            label={`Updated ${Math.round((Date.now() - lastRefresh.getTime()) / 1000)}s ago`}
            size="small"
            variant="outlined"
            color={refreshing ? 'primary' : 'default'}
          />
          <IconButton onClick={handleRefresh} size="small" disabled={refreshing}>
            <RefreshIcon className={refreshing ? 'rotating' : ''} />
          </IconButton>
        </Box>
      </Box>

      {/* Tabs */}
      <Card sx={{ mb: 2 }}>
        <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)} variant="scrollable" scrollButtons="auto">
          <Tab label="Overview" />
          <Tab label="Registry" />
          <Tab label={`Instances ${instances.length > 0 ? `(${instances.length})` : ''}`} />
          <Tab label={`Performance`} />
          <Tab label={`Activity ${events.length > 0 ? `(${events.length})` : ''}`} />
          <Tab label={`History ${statistics ? `(${statistics.totalThreads})` : ''}`} />
        </Tabs>
      </Card>

      {/* Content */}
      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400, gap: 2 }}>
          <CircularProgress />
          <Typography color="text.secondary">Loading AI Observatory data...</Typography>
        </Box>
      ) : (
        <>
          {/* Overview Tab */}
          {selectedTab === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <StatisticsPanel statistics={statistics} snapshot={snapshot} />
              </Grid>
              <Grid item xs={12} md={6}>
                <AgentRegistryPanel registry={registry} />
              </Grid>
              <Grid item xs={12} md={6}>
                <AgentInstancesPanel instances={instances.slice(0, 10)} />
              </Grid>
              {events.length > 0 && (
                <Grid item xs={12}>
                  <ActivityStreamPanel events={events.slice(0, 10)} onClearEvents={clearEvents} />
                </Grid>
              )}
            </Grid>
          )}

          {/* Registry Tab */}
          {selectedTab === 1 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AgentRegistryPanel registry={registry} />
              </Grid>
            </Grid>
          )}

          {/* Instances Tab */}
          {selectedTab === 2 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AgentInstancesPanel instances={instances} />
              </Grid>
            </Grid>
          )}

          {/* Performance Tab */}
          {selectedTab === 3 && (
            <PerformancePanel />
          )}

          {/* Activity Tab */}
          {selectedTab === 4 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <ActivityStreamPanel events={events} onClearEvents={clearEvents} />
              </Grid>
            </Grid>
          )}

          {/* History Tab */}
          {selectedTab === 5 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <HistoryPanel />
              </Grid>
            </Grid>
          )}
        </>
      )}
    </Box>
  );
};
